<script setup lang="ts">
import { USER_V2_FIELD_CLASS as fieldClass } from './types';
import type { SelfExclusionPayload } from '@/types/UserLimits';

// Tela "Auto-Exclusão" do Minha Conta V2 (ENG-362).
// Visual: card escuro "Auto Exclusão" (Figma 19771:42100 mobile /
// 19771:52549 desktop). O fluxo reusa a lógica real da página antiga de Jogo
// Responsável (components/user/account/Protection/SelfExclusion.vue):
// useUserLimits().setSelfExclusion com validação de containment
// ('update_limits'), kyc_id e password_check — mesmo padrão já usado em
// ScreenTemporaryPause.vue. Antes desta correção o submit só disparava um
// toaster fake (ver pages/user/self/self-exclusion.vue, mock pré-existente).
const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { getDateTime } = useDateTimeHelper();
const { fetchUserProfile } = useUserData();

const {
  userLimits,
  setSelfExclusion,
  userExclusionMinOptionVal,
  userIsOnExclusionPermanentActive,
} = useUserLimits();

// tela de propósito único (só este card) — já chega expandida, senão o
// usuário precisaria clicar de novo pra ver o conteúdo da tela que abriu.
const isExpanded = ref(true);
const isLoading = ref(false);

const selfExclusionActive = ref<number>(0);
const selfExclusionPeriod = ref<string>('0');
const selfExclusionReason = ref<string>('0');
const confirmed = ref(false);

const choiceOptions = [
  { value: 0, label: t('general.no') },
  { value: 1, label: t('general.yes') },
];

// mesmos períodos (em meses) e mesmo mínimo regulatório de
// Protection/SelfExclusion.vue — não inventar valores novos aqui (ex.: "1
// mês" do mock antigo) porque a API só aceita este conjunto.
const exclusionOptions = [
  { value: '0', label: t('account.self_exclusion.select_period') },
  { value: '3', label: `3 ${t('general.months')}` },
  { value: '6', label: `6 ${t('general.months')}` },
  { value: '12', label: `1 ${t('general.year')}` },
  { value: '24', label: `2 ${t('general.years')}` },
  { value: '36', label: `3 ${t('general.years')}` },
  { value: '48', label: `4 ${t('general.years')}` },
  { value: '60', label: `5 ${t('general.years')}` },
  { value: '99', label: t('account.self_exclusion.permanent') },
];

const reasonOptions = [
  { value: '0', label: t('account.self_exclusion.reason_0') },
  { value: '1', label: t('account.self_exclusion.reason_1') },
  { value: '2', label: t('account.self_exclusion.reason_2') },
  { value: '3', label: t('account.self_exclusion.reason_3') },
  { value: '4', label: t('account.self_exclusion.reason_4') },
  { value: '5', label: t('account.self_exclusion.reason_5') },
  { value: '6', label: t('account.self_exclusion.reason_6') },
  { value: '7', label: t('account.self_exclusion.reason_7') },
  { value: '8', label: t('account.self_exclusion.reason_8') },
  { value: '9', label: t('account.self_exclusion.reason_9') },
];

const exclusionOptionsFiltered = computed(() => {
  return exclusionOptions.filter(
    (option) =>
      Number(option.value) >= Number(userExclusionMinOptionVal.value) ||
      option.value === '0',
  );
});

// exclusão ativa não pode ser removida por aqui, só consultada
const isDisabledRemoveExclusion = computed(() => {
  return !!userLimits.value?.user_exclusion;
});

const isDisabledExclusionButton = computed(() => {
  return (
    isLoading.value ||
    !confirmed.value ||
    !selfExclusionPeriod.value ||
    !selfExclusionReason.value ||
    selfExclusionPeriod.value === '0' ||
    selfExclusionReason.value === '0'
  );
});

function getExclusionButtonText() {
  if (selfExclusionPeriod.value === '0') {
    return t('account.self_exclusion.select_period');
  }

  if (selfExclusionReason.value === '0') {
    return t('account.self_exclusion.reason_0');
  }

  if (selfExclusionPeriod.value === '99') {
    return t('account.self_exclusion.permanent_button');
  }

  return t('account.self_exclusion_exclude_account', {
    period:
      exclusionOptions.find(
        (option) => option.value === selfExclusionPeriod.value,
      )?.label || '',
  });
}

async function handleSubmit() {
  if (selfExclusionPeriod.value === '0' || !selfExclusionPeriod.value) {
    openToaster({
      message: t('account.self_exclusion.select_period'),
      type: 'error',
      time: 10000,
    });

    return;
  }

  if (selfExclusionReason.value === '0' || !selfExclusionReason.value) {
    openToaster({
      message: t('account.self_exclusion.reason_0'),
      type: 'error',
      time: 10000,
    });

    return;
  }

  await $containment.handle('update_limits', () => {
    sendSubmit();
  });
}

async function sendSubmit() {
  if (!selfExclusionPeriod.value) {
    return;
  }

  if (!selfExclusionReason.value) {
    return;
  }

  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  isLoading.value = true;

  const payload: SelfExclusionPayload = {
    user_exclusion: Number(selfExclusionPeriod.value),
    reason: Number(selfExclusionReason.value),
  };

  if ($kyc?.current?.id) {
    payload.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    payload.password_check = temporaryPassword.get();
  }

  await setSelfExclusion(payload)
    .then((res: any) => {
      const message =
        res.data?.message || t('account.timeout_limits.update_success');

      openToaster({
        message,
        type: 'success',
      });
    })
    .catch((error: any) => {
      const message =
        error.data?.message ||
        error.message ||
        t('account.timeout_limits.generic_error');

      openToaster({
        message,
        type: 'error',
        time: 12000,
      });
    })
    .finally(() => {
      isLoading.value = false;
      temporaryPassword.clear();
    });
}

watch(
  userLimits,
  () => {
    if (userLimits.value?.user_exclusion) {
      selfExclusionActive.value = 1;
      selfExclusionPeriod.value = String(userLimits.value.user_exclusion);
    } else {
      selfExclusionActive.value = 0;
    }
  },
  { immediate: true },
);

onBeforeMount(async () => {
  if (!userLimits.value?.user_exclusion_at) {
    await fetchUserProfile({ force: true });
  }
});
</script>

<template>
  <div class="flex flex-col gap-[8px]">
    <!-- card "Auto Exclusão" (accordion) -->
    <button
      class="flex w-full items-center gap-[8px] rounded-[10px] bg-v2-default/40 p-[12px]"
      type="button"
      @click="isExpanded = !isExpanded"
    >
      <span
        class="flex size-[32px] shrink-0 items-center justify-center rounded-[10px] bg-v2-default/40"
      >
        <UserV2Icon name="user-block" />
      </span>
      <span class="flex h-[36px] min-w-0 flex-1 items-center text-left">
        <span class="text-[14px] font-medium leading-[20px] text-white">
          {{ t('account.self_exclusion.title') }}
        </span>
      </span>
      <UserV2Icon
        :class="{ 'rotate-180': isExpanded }"
        name="chevron-down"
      />
    </button>

    <!-- fluxo da página antiga dentro do visual novo -->
    <div
      v-if="isExpanded"
      class="flex flex-col gap-[16px] rounded-[10px] bg-v2-default/40 p-[16px]"
    >
      <p class="text-[12px] leading-[16px] text-v2-default-900">
        {{ t('account.self_exclusion.text') }}
      </p>

      <template v-if="!userIsOnExclusionPermanentActive">
        <div class="flex flex-col gap-[8px]">
          <p class="text-[12px] leading-[16px] text-v2-default-700">
            {{ t('account.self_exclusion.set_exclusion') }}
          </p>
          <div class="flex gap-[8px]">
            <button
              v-for="option in choiceOptions"
              :key="option.value"
              class="h-[32px] rounded-[12px] px-[16px] text-[13px] disabled:cursor-not-allowed disabled:opacity-50"
              :class="
                selfExclusionActive === option.value
                  ? 'bg-v2-primary text-white'
                  : 'bg-v2-default/40 text-v2-foreground'
              "
              :disabled="isDisabledRemoveExclusion"
              type="button"
              @click="selfExclusionActive = option.value"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <template v-if="!!selfExclusionActive">
          <div class="flex flex-col gap-[4px]">
            <label
              class="text-[12px] leading-[16px] text-v2-default-700"
              for="self-exclusion-period"
            >
              {{ t('account.self_exclusion.period') }}
            </label>
            <select
              id="self-exclusion-period"
              v-model="selfExclusionPeriod"
              :class="fieldClass"
            >
              <option
                v-for="option in exclusionOptionsFiltered"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-[4px]">
            <label
              class="text-[12px] leading-[16px] text-v2-default-700"
              for="self-exclusion-reason"
            >
              {{ t('account.self_exclusion.reason') }}
            </label>
            <select
              id="self-exclusion-reason"
              v-model="selfExclusionReason"
              :class="fieldClass"
            >
              <option
                v-for="option in reasonOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </template>

        <p
          v-if="userLimits?.user_exclusion"
          class="text-[12px] leading-[16px] text-v2-warning/70"
        >
          {{ t('account.account_protection.last_change') }}
          <strong>{{ getDateTime(userLimits?.user_exclusion_at) }}</strong>
        </p>

        <label
          v-if="!!selfExclusionActive && !isDisabledRemoveExclusion"
          class="flex cursor-pointer items-start gap-[8px] text-[12px] leading-[16px] text-v2-foreground"
          for="self-exclusion-confirm"
        >
          <input
            id="self-exclusion-confirm"
            v-model="confirmed"
            class="mt-[2px] size-[14px] shrink-0 accent-v2-primary"
            type="checkbox"
          />
          {{ t('account.self_exclusion.confirm_irreversible') }}
        </label>

        <button
          v-if="!!selfExclusionActive"
          class="flex h-[40px] w-full items-center justify-center gap-[8px] rounded-[12px] bg-v2-danger/20 text-[14px] text-v2-danger disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isDisabledExclusionButton"
          type="button"
          @click="handleSubmit"
        >
          {{ getExclusionButtonText() }}
        </button>
      </template>

      <p
        v-else
        class="rounded-[12px] bg-v2-danger/20 p-[12px] text-[12px] leading-[16px] text-v2-danger"
        v-html="t('account.self_exclusion.exclusion_permanent_active_alert')"
      />
    </div>
  </div>
</template>
